/**
 * Phase 14.15 — Superadmin admin queue for brochure leads.
 *
 * Lists `pending_email = True` brochure-form submissions so a
 * superadmin can manually email the download link until an
 * automated mailer is wired (~30 LOC follow-up). Mirrors the
 * patterns used by other superadmin pages in the workbench.
 *
 * Each row shows: timestamp, name, email, organisation, copy-link
 * button, "mark sent" button. The download URL is the same opaque
 * tokenised path served by `/api/brochure/download/{token}` —
 * valid for 24h from form submission.
 *
 * Access gate: `require_active_tenant` + role check on the backend
 * returns 403 for non-superadmin users; this page degrades to an
 * inline error if the API rejects.
 */
import { useEffect, useState } from "react";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatApiError } from "@/lib/formatApiError";

export default function AdminBrochureLeadsPage() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [busyId, setBusyId] = useState("");
    const [copiedId, setCopiedId] = useState("");

    async function load() {
        setLoading(true);
        setErr("");
        try {
            const res = await api.get("/api/admin/brochure-leads");
            setRows(res.data?.items || []);
        } catch (e) {
            setErr(formatApiError(e, "Failed to load brochure-lead queue"));
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => {
        load();
    }, []);

    async function copyLink(row) {
        const base =
            (typeof window !== "undefined" && window.location?.origin) || "";
        const url = `${base}${row.download_url}`;
        try {
            await navigator.clipboard.writeText(url);
            setCopiedId(row.id);
            setTimeout(() => setCopiedId(""), 1500);
        } catch (_e) {
            // Fallback — surface the URL inline so the operator can
            // copy it manually if the clipboard API is blocked.
            window.prompt("Copy this URL", url);
        }
    }

    async function markSent(row) {
        if (busyId) return;
        setBusyId(row.id);
        try {
            await api.post(
                `/api/admin/brochure-leads/${row.id}/mark-sent`,
            );
            // Remove the row locally — the queue only shows
            // pending_email=true rows.
            setRows((r) => r.filter((x) => x.id !== row.id));
        } catch (e) {
            setErr(formatApiError(e, "Failed to mark lead as sent"));
        } finally {
            setBusyId("");
        }
    }

    return (
        <div
            className="p-6 max-w-6xl mx-auto space-y-4"
            data-testid="admin-brochure-leads-page"
        >
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold">
                        Brochure leads — pending email queue
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        Operators who submitted the brochure-request form and
                        haven't yet been emailed the download link.
                    </p>
                </div>
                <Button
                    variant="outline"
                    onClick={load}
                    disabled={loading}
                    data-testid="admin-brochure-leads-refresh"
                >
                    Refresh
                </Button>
            </div>

            {err ? (
                <div
                    className="rounded border border-red-700 bg-red-900/20 p-3 text-sm text-red-200"
                    data-testid="admin-brochure-leads-error"
                >
                    {err}
                </div>
            ) : null}

            {loading ? (
                <Skeleton className="h-48 w-full" />
            ) : rows.length === 0 ? (
                <div
                    className="rounded border border-dashed border-slate-700 bg-slate-900/30 p-8 text-center text-sm text-muted-foreground"
                    data-testid="admin-brochure-leads-empty"
                >
                    No leads in the email queue.
                </div>
            ) : (
                <div className="rounded border border-slate-800 overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-900/60 text-slate-300">
                            <tr>
                                <th className="text-left px-3 py-2">
                                    Submitted
                                </th>
                                <th className="text-left px-3 py-2">Name</th>
                                <th className="text-left px-3 py-2">Email</th>
                                <th className="text-left px-3 py-2">
                                    Organisation
                                </th>
                                <th className="text-left px-3 py-2">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr
                                    key={row.id}
                                    className="border-t border-slate-800"
                                    data-testid={`admin-brochure-leads-row-${row.id}`}
                                >
                                    <td className="px-3 py-2 whitespace-nowrap">
                                        {row.submitted_at}
                                    </td>
                                    <td className="px-3 py-2">{row.name}</td>
                                    <td className="px-3 py-2">{row.email}</td>
                                    <td className="px-3 py-2">
                                        {row.organisation}
                                    </td>
                                    <td className="px-3 py-2 space-x-2">
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => copyLink(row)}
                                            data-testid={`admin-brochure-leads-copy-${row.id}`}
                                        >
                                            {copiedId === row.id
                                                ? "Copied"
                                                : "Copy link"}
                                        </Button>
                                        <Button
                                            size="sm"
                                            onClick={() => markSent(row)}
                                            disabled={busyId === row.id}
                                            data-testid={`admin-brochure-leads-mark-sent-${row.id}`}
                                        >
                                            {busyId === row.id
                                                ? "..."
                                                : "Mark sent"}
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
